<template>
  <div class="commission-page">
    <b-container fluid class="py-4">

      <!-- Header -->
      <div class="page-header d-flex align-items-center justify-content-between mb-3">
        <div>
          <h2 class="mb-0">Справочник комиссий</h2>
          <small class="text-muted">Настройка ступенчатых ставок комиссии по диапазонам сумм</small>
        </div>
        <b-button variant="primary" class="shadow-sm" @click="openCreate">
          <b-icon icon="plus-circle" class="mr-1" /> Новый диапазон
        </b-button>
      </div>

      <!-- Content -->
      <b-row>
        <b-col lg="5" xl="4" class="mb-4">
          <!-- Create / Edit Card -->
          <b-card class="shadow-soft h-100">
            <template #header>
              <div class="d-flex align-items-center">
                <b-icon :icon="isEditing ? 'pencil-square' : 'layers'" class="mr-2"></b-icon>
                <span>{{ isEditing ? 'Редактировать диапазон комиссии' : 'Добавить диапазон комиссии' }}</span>
              </div>
            </template>

            <b-form @submit.prevent="saveCommission">
              <b-form-group label="Сумма от" label-for="from-amount" description="Нижняя граница (включительно)">
                <b-input-group>
                  <b-input-group-prepend is-text>₸</b-input-group-prepend>
                  <b-form-input
                    id="from-amount"
                    v-model.number="draft.fromAmount"
                    type="number"
                    min="0"
                    step="0.01"
                    required
                    placeholder="0.00"
                  />
                </b-input-group>
              </b-form-group>

              <b-form-group label="Сумма до" label-for="to-amount" description="Верхняя граница (включительно)">
                <b-input-group>
                  <b-input-group-prepend is-text>₸</b-input-group-prepend>
                  <b-form-input
                    id="to-amount"
                    v-model.number="draft.toAmount"
                    type="number"
                    :min="draft.fromAmount || 0"
                    step="0.01"
                    required
                    placeholder="10000.00"
                  />
                </b-input-group>
              </b-form-group>

              <b-form-group label="Ставка" label-for="rate" description="Процент, применяемый к данному диапазону">
                <b-input-group>
                  <b-form-input
                    id="rate"
                    v-model.number="draft.percent"
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    required
                    placeholder="0.50"
                  />
                  <b-input-group-append is-text>%</b-input-group-append>
                </b-input-group>
              </b-form-group>

              <b-form-group label="Название сегмента" label-for="segment" description="Например: Retail / Mass market / Active / Pro. Видно клиенту на /commission">
                <b-form-input
                  id="segment"
                  v-model="draft.segmentName"
                  type="text"
                  maxlength="64"
                  placeholder="Retail"
                />
              </b-form-group>

              <b-form-group label="Комментарий" label-for="comment" description="Краткое описание сегмента — видно клиенту в правой колонке таблицы">
                <b-form-textarea
                  id="comment"
                  v-model="draft.comment"
                  rows="2"
                  max-rows="4"
                  maxlength="255"
                  placeholder="Розничные клиенты, высокий операционный overhead"
                />
              </b-form-group>

              <b-alert
                v-if="rangeError"
                show
                variant="danger"
                class="py-2 px-3"
              >
                {{ rangeError }}
              </b-alert>

              <div class="d-flex align-items-center justify-content-between mt-3">
                <b-button type="submit" variant="success" class="px-4 shadow-sm" :disabled="!!rangeError">
                  <b-icon :icon="isEditing ? 'check2-circle' : 'plus-circle'" class="mr-1" />
                  {{ isEditing ? 'Сохранить изменения' : 'Добавить комиссию' }}
                </b-button>
                <b-button v-if="isEditing" variant="outline-secondary" class="px-3" @click="cancelEdit">
                  Отмена
                </b-button>
              </div>
            </b-form>
          </b-card>
        </b-col>

        <b-col lg="7" xl="8" class="mb-4">
          <!-- Table Card -->
          <b-card class="shadow-soft">
            <template #header>
              <div class="d-flex align-items-center justify-content-between">
                <span><b-icon icon="table" class="mr-2" />Диапазоны комиссий</span>
                <b-input-group size="sm" class="w-auto">
                  <b-input-group-prepend is-text>
                    <b-icon icon="search" />
                  </b-input-group-prepend>
                  <b-form-input v-model="search" placeholder="Поиск…" />
                </b-input-group>
              </div>
            </template>

            <b-table
              :items="filteredCommissions"
              :fields="fields"
              small
              responsive="md"
              hover
              head-variant="light"
              class="mb-2 table-rounded"
              :tbody-tr-class="rowClass"
            >
              <template #cell(range)="data">
                <b-badge pill variant="light" class="range-pill">
                  {{ asMoney(data.item.fromAmount) }} — {{ asMoney(data.item.toAmount) }}
                </b-badge>
              </template>

              <template #cell(percent)="data">
                <b-badge :variant="rateVariant(data.item.percent)" class="px-3 py-2">
                  {{ data.item.percent }}%
                </b-badge>
              </template>

              <template #cell(actions)="data">
                <b-button size="sm" variant="outline-primary" class="mr-2" @click="startEdit(data.item)">
                  <b-icon icon="pencil"></b-icon>
                </b-button>
                <b-button size="sm" variant="outline-danger" @click="confirmDelete(data.item)">
                  <b-icon icon="trash"></b-icon>
                </b-button>
              </template>
            </b-table>

            <div v-if="!filteredCommissions.length" class="text-center text-muted py-4">
              <b-icon icon="inbox" font-scale="1.2"></b-icon>
              <div class="mt-2">Диапазоны комиссий не найдены</div>
            </div>
          </b-card>
        </b-col>
      </b-row>

      <!-- Delete confirmation -->
      <b-modal id="del-confirm" ref="delConfirm" title="Удалить диапазон?" centered ok-variant="danger" @ok="deleteTier">
        Действие необратимо. Удалить этот диапазон комиссии?
      </b-modal>

      <!-- Toast container (BootstrapVue toasts will use this) -->
      <b-toaster name="b-toaster-top-right"></b-toaster>
    </b-container>
  </div>
</template>

<script>
import axios from 'axios';
import { apiDomain, getHeader } from '@/config/config';

export default {
  name: 'CommissionRef',
  data() {
    return {
      commissions: [],
      draft: { id: null, fromAmount: 0, toAmount: 0, percent: 0, segmentName: '', comment: '' },
      isEditing: false,
      toDelete: null,
      search: '',
      fields: [
        { key: 'range', label: 'Диапазон сумм', sortable: false, class: 'align-middle' },
        { key: 'percent', label: 'Ставка', sortable: true, class: 'align-middle text-center', thClass: 'text-center' },
        { key: 'actions', label: '', class: 'text-right', thClass: 'text-right', sortable: false }
      ]
    };
  },
  computed: {
    filteredCommissions() {
      const s = this.search.trim().toLowerCase();
      const list = [...this.commissions].sort((a, b) => a.fromAmount - b.fromAmount);
      if (!s) return list;
      return list.filter(it =>
        String(it.fromAmount).includes(s) ||
        String(it.toAmount).includes(s) ||
        String(it.percent).includes(s)
      );
    },
    rangeError() {
      const { fromAmount, toAmount, percent } = this.draft;
      if (fromAmount == null || toAmount == null || percent == null) return null;
      if (fromAmount < 0 || toAmount < 0) return 'Суммы должны быть неотрицательными.';
      if (toAmount < fromAmount) return '«Сумма до» должна быть больше или равна «Сумма от».';
      if (percent < 0 || percent > 100) return 'Процент должен быть в диапазоне 0–100%.';

      // overlap check against existing tiers (ignore self when editing)
      const id = this.draft.id;
      const overlap = this.commissions.some(t => {
        if (id && t.id === id) return false;
        const a1 = fromAmount, a2 = toAmount;
        const b1 = t.fromAmount, b2 = t.toAmount;
        return Math.max(a1, b1) <= Math.min(a2, b2);
      });
      if (overlap) return 'Этот диапазон пересекается с существующим.';
      return null;
    }
  },
  mounted() {
    this.fetchCommissions();
  },
  methods: {
    asMoney(v) {
      if (v == null) return '—';
      return Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    rateVariant(rate) {
      if (rate >= 5) return 'danger';
      if (rate >= 2) return 'warning';
      return 'success';
    },
    rowClass() {
      return 'row-hover';
    },

    async fetchCommissions() {
      try {
        const { data } = await axios.get(`${apiDomain}/exchange/commission/list`, { headers: getHeader() });
        this.commissions = Array.isArray(data) ? data : [];
      } catch (e) {
        this.toast('Не удалось загрузить комиссии', 'danger');
        // eslint-disable-next-line no-console
        console.error('Error fetching commissions:', e);
      }
    },

    async saveCommission() {
      if (this.rangeError) return;
      try {
        if (this.isEditing) {
          await axios.post(`${apiDomain}/exchange/commission/update`, this.draft, { headers: getHeader() });
          this.toast('Комиссия обновлена', 'success');
        } else {
          await axios.post(`${apiDomain}/exchange/commission/create`, {
            fromAmount: this.draft.fromAmount,
            toAmount: this.draft.toAmount,
            percent: this.draft.percent,
            segmentName: this.draft.segmentName || null,
            comment: this.draft.comment || null,
          }, { headers: getHeader() });
          this.toast('Комиссия добавлена', 'success');
        }
        await this.fetchCommissions();
        this.cancelEdit();
      } catch (e) {
        this.toast('Не удалось сохранить', 'danger');
        // eslint-disable-next-line no-console
        console.error('Error saving commission:', e);
      }
    },

    startEdit(item) {
      this.isEditing = true;
      this.draft = { ...item };
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    cancelEdit() {
      this.isEditing = false;
      this.draft = { id: null, fromAmount: 0, toAmount: 0, percent: 0, segmentName: '', comment: '' };
    },

    openCreate() {
      this.cancelEdit();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },

    confirmDelete(item) {
      this.toDelete = item;
      this.$refs.delConfirm.show();
    },
    async deleteTier() {
      if (!this.toDelete) return;
      try {
        await axios.post(`${apiDomain}/exchange/commission/delete`, { id: this.toDelete.id }, { headers: getHeader() });
        this.toast('Комиссия удалена', 'success');
        await this.fetchCommissions();
      } catch (e) {
        this.toast('Не удалось удалить', 'danger');
        // eslint-disable-next-line no-console
        console.error('Error deleting commission:', e);
      } finally {
        this.toDelete = null;
      }
    },

    toast(msg, variant = 'default') {
      this.$bvToast.toast(msg, {
        title: 'Комиссии',
        variant,
        solid: true,
        toaster: 'b-toaster-top-right',
        autoHideDelay: 2000
      });
    }
  }
};
</script>

<style scoped>
/* Layout polish */
.commission-page {
  background: linear-gradient(180deg, #f8fafc 0%, #ffffff 100%);
  min-height: 100%;
}
.page-header h2 {
  font-weight: 700;
  letter-spacing: -0.02em;
}
.shadow-soft {
  border: 1px solid #eef2f7;
  box-shadow: 0 10px 30px rgba(18, 38, 63, 0.06);
  border-radius: 16px;
}
.range-pill {
  background: #f5f7fb !important;
  border: 1px solid #e7ecf4;
  font-weight: 600;
}
.table-rounded {
  border-radius: 12px;
  overflow: hidden;
}
.row-hover:hover {
  background: #f9fbff !important;
}

/* Inputs */
.form-control, .custom-select, .input-group-text {
  border-radius: 12px !important;
}
.input-group-text {
  background: #f7f9fc;
  border-color: #e9eef5;
}
</style>

<!-- Global (non-scoped) tweaks for header bg -->
<style>
.card-header {
  background: #ffffff;
  border-bottom: 1px solid #eef2f7;
  font-weight: 600;
}
</style>
